import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { TranslatePipe } from '../../filters/translate.pipe';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import RoutingService from '../../services/routing.service';
import ToolkitService from '../../services/toolkit.service';
import Session from '../../utils/session.service';
import { parseJsonLenient } from '../../utils/toolkit-json.util';

@Component({
	standalone: true,
	selector: 'my-toolkit',
	templateUrl: './my-toolkit.component.html',
	imports: [CommonModule, FormsModule, TranslatePipe, LoaderOverlayComponent]
})
export default class MyToolkitComponent implements OnInit {
	private routingService: RoutingService = inject(RoutingService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private session: Session = inject(Session);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.routingService.validateAccess('myExercises').then(() => this.onResolve());
	}

	onResolve() {
		this.vm.user = this.session.getSession()?.user;
		this.vm.loading = true;
		this.vm.error = false;

		this.vm.catalogue = null;
		this.vm.resources = [];
		this.vm.assessments = [];
		this.vm.themes = [];

		this.vm.exerciseAssignments = [];
		this.vm.surveyAssignments = [];

		this.vm.search = '';
		this.vm.kind = 'ALL';
		this.vm.activeTab = this.vm.activeTab ?? 'pending';

		this.vm.filtered = [];
		this.vm.filteredExercises = [];
		this.vm.filteredSurveys = [];
		this.vm.pendingExercises = [];
		this.vm.pendingSurveys = [];
		this.vm.historyExercises = [];
		this.vm.historySurveys = [];
		this.vm.exerciseRows = [];
		this.vm.surveyRows = [];
		this.vm.allRows = [];
		this.vm.hasRows = false;

		this.vm.applyFilters = this.applyFilters.bind(this);
		this.vm.openItem = this.openItem.bind(this);
		this.vm.initials = this.initials.bind(this);
		this.vm.themeKey = this.themeKey.bind(this);
		this.vm.resourceFormatKey = this.resourceFormatKey.bind(this);
		this.vm.surveyDetails = this.surveyDetails.bind(this);
		this.vm.selectTab = this.selectTab.bind(this);

		let loadedCatalogue = false;
		let loadedAssignments = false;
		const done = () => {
			if (!loadedCatalogue || !loadedAssignments) return;
			this.vm.loading = false;
			this.applyFilters();
		};

		this.toolkitService.getCatalogue(
			{},
			(catalogue) => {
				this.vm.catalogue = catalogue;
				this.vm.themes = Array.isArray(catalogue?.themes) ? catalogue.themes : [];
				this.vm.resources = Array.isArray(catalogue?.resources) ? catalogue.resources : [];
				this.vm.assessments = Array.isArray(catalogue?.assessments) ? catalogue.assessments : [];
				loadedCatalogue = true;
				done();
			},
			() => {
				loadedCatalogue = true;
				this.vm.error = true;
				done();
			}
		);

		Promise.all([
			this.toolkitService.getMyAssignmentsList({}).catch(() => []),
			this.toolkitService.getMyAssessmentAssignmentsList({}).catch(() => [])
		])
			.then(([ex, sv]) => {
				this.vm.exerciseAssignments = Array.isArray(ex) ? ex : [];
				this.vm.surveyAssignments = Array.isArray(sv) ? sv : [];
				loadedAssignments = true;
				done();
			})
			.catch(() => {
				loadedAssignments = true;
				this.vm.error = true;
				done();
			});
	}

	initials(): string {
		const u = this.vm.user || {};
		const f = (u?.firstname || u?.firstName || '?').toString().trim();
		const l = (u?.lastname || u?.lastName || '').toString().trim();
		return `${f ? f[0] : '?'}${l ? l[0] : ''}`.toUpperCase();
	}

	private isOverdue(dueAt: any): boolean {
		if (!dueAt) return false;
		const d = new Date(dueAt);
		if (!isFinite(d.getTime())) return false;
		const today = new Date();
		const t = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
		const due = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
		return due < t;
	}

	private deriveExerciseStatus(a: any): string {
		const completions = Array.isArray(a?.completions) ? a.completions : [];
		if (a?.completedAt) return 'completed';
		if (completions.length > 0) return 'completed';
		if (this.isOverdue(a?.dueAt)) return 'overdue';
		return 'pending';
	}

	private deriveSurveyStatus(a: any): string {
		const status = (a?.status || '').toString().toLowerCase();
		if (status === 'completed' || a?.completedAt) return 'completed';
		if (this.isOverdue(a?.dueAt)) return 'overdue';
		return 'pending';
	}

	private resourceById(id: any): any | null {
		const rid = (id ?? '').toString();
		if (!rid) return null;
		return (this.vm.resources || []).find((r) => (r?.id ?? '').toString() === rid) || null;
	}

	private assessmentById(id: any): any | null {
		const aid = (id ?? '').toString();
		if (!aid) return null;
		return (this.vm.assessments || []).find((a) => (a?.id ?? '').toString() === aid) || null;
	}

	private sortKey(a: any): number {
		const due = a?.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY;
		const assigned = a?.assignedAt ? new Date(a.assignedAt).getTime() : Number.POSITIVE_INFINITY;
		const id = Number(a?.id || 0);
		return due !== Number.POSITIVE_INFINITY ? due : assigned !== Number.POSITIVE_INFINITY ? assigned : id;
	}

	private historySortKey(a: any): number {
		const completed = a?.completedAt ? new Date(a.completedAt).getTime() : Number.NEGATIVE_INFINITY;
		const updated = a?.updatedAt ? new Date(a.updatedAt).getTime() : Number.NEGATIVE_INFINITY;
		const assigned = a?.assignedAt ? new Date(a.assignedAt).getTime() : Number.NEGATIVE_INFINITY;
		const id = Number(a?.id || 0);
		return completed !== Number.NEGATIVE_INFINITY ? completed : updated !== Number.NEGATIVE_INFINITY ? updated : assigned !== Number.NEGATIVE_INFINITY ? assigned : id;
	}

	themeKey(themeId: any): string {
		const id = (themeId ?? '').toString();
		if (!id) return '';
		const themes: any[] = Array.isArray(this.vm.themes) ? this.vm.themes : [];
		const found = themes.find((t) => (t?.id ?? '').toString() === id) || null;
		const raw = (found?.name ?? found?.id ?? id).toString();
		// Match ToolkitComponent mappings so existing dictionary keys are used.
		const s = raw
			.toString()
			.trim()
			.toLowerCase()
			.replace(/&/g, 'and')
			.replace(/[^\w\s]/g, ' ')
			.replace(/[_-]/g, ' ')
			.replace(/\s+/g, ' ');
		if (!s) return raw;
		if (s === 'anxiety') return 'Toolkit.Theme.Anxiety';
		if (s === 'stress' || s === 'stress and overload' || s === 'stress overload' || s === 'overload') return 'Toolkit.Theme.Stress';
		if (s === 'low mood' || s === 'lowmood') return 'Toolkit.Theme.LowMood';
		if (s === 'sleep') return 'Toolkit.Theme.Sleep';
		if (s === 'self esteem' || s === 'selfesteem') return 'Toolkit.Theme.SelfEsteem';
		if (s === 'habits') return 'Toolkit.Theme.Habits';
		if (s === 'motivation') return 'Toolkit.Theme.Motivation';
		if (s === 'emotional regulation') return 'Toolkit.Theme.EmotionalRegulation';
		if (s === 'focus and concentration' || s === 'focus concentration') return 'Toolkit.Theme.FocusConcentration';
		if (s === 'relationships') return 'Toolkit.Theme.Relationships';
		return raw;
	}

	resourceFormatKey(format: any): string {
		const f = (format ?? '').toString();
		if (!f) return '';
		if (f === 'psychoeducation') return 'Toolkit.Format.Psychoeducation';
		if (f === 'exercise') return 'Toolkit.Format.Exercise';
		if (f === 'worksheet') return 'Toolkit.Format.Worksheet';
		if (f === 'check-in' || f === 'checkin') return 'Toolkit.Format.CheckIn';
		if (f === 'planner') return 'Toolkit.Format.Planner';
		return f;
	}

	surveyDetails(assessment: any, assignment: any): { code: string; fullTitle: string; itemCount: number; durationMinutes: number | null; note: string } {
		const note = (assignment?.note || '').toString().trim();
		const durationMinutes = assessment?.durationMinutes ?? assessment?.duration_minutes ?? null;

		const raw = assessment?.questionsJson || assessment?.questions_json || assignment?.assessmentQuestionsSnapshotJson || null;
		const parsed = parseJsonLenient(raw);
		const instrument = parsed?.instrument && typeof parsed.instrument === 'object' ? parsed.instrument : parsed;
		const code = (instrument?.code || parsed?.code || assessment?.code || '').toString();
		const fullTitle = (instrument?.fullTitle || instrument?.title || parsed?.fullTitle || parsed?.title || '').toString();
		const items = Array.isArray(instrument?.items) ? instrument.items : Array.isArray(parsed?.items) ? parsed.items : [];
		const rawItemCount =
			instrument?.itemCount ??
			instrument?.item_count ??
			parsed?.itemCount ??
			parsed?.item_count ??
			assessment?.itemCount ??
			assessment?.item_count ??
			items.length;
		const numericItemCount = Number(rawItemCount);
		const itemCount = isFinite(numericItemCount) && numericItemCount > 0 ? numericItemCount : items.length;

		return { code, fullTitle, itemCount, durationMinutes: durationMinutes != null ? Number(durationMinutes) : null, note };
	}

	applyFilters() {
		const q = (this.vm.search || '').toString().trim().toLowerCase();
		const kind = (this.vm.kind || 'ALL').toString();

		const exRows: any[] = [];
		const svRows: any[] = [];

		(this.vm.exerciseAssignments || []).forEach((a) => {
			if (a?.isDeleted) return;
			const res = this.resourceById(a?.resourceId);
			const title = (res?.title || res?.name || a?.resourceId || '').toString();
			exRows.push({
				kind: 'EXERCISE',
				assignmentId: a?.id,
				itemId: a?.resourceId,
				title,
				summary: (res?.summary || res?.description || '').toString(),
				themeId: res?.themeId ?? res?.theme_id ?? res?.theme ?? null,
				format: res?.format ?? res?.type ?? null,
				durationMinutes: res?.durationMinutes ?? res?.duration_minutes ?? res?.duration ?? null,
				note: (a?.note || '').toString().trim(),
				dueAt: a?.dueAt || null,
				status: this.deriveExerciseStatus(a),
				raw: a
			});
		});

		(this.vm.surveyAssignments || []).forEach((a) => {
			if (a?.isDeleted) return;
			const asmt = this.assessmentById(a?.assessmentId);
			const det = this.surveyDetails(asmt, a);
			const title = (det.fullTitle || asmt?.title || asmt?.name || a?.assessmentId || '').toString();
			svRows.push({
				kind: 'SURVEY',
				assignmentId: a?.id,
				itemId: a?.assessmentId,
				title,
				description: (asmt?.description || asmt?.summary || '').toString(),
				code: det.code,
				itemCount: det.itemCount,
				durationMinutes: det.durationMinutes,
				note: det.note,
				dueAt: a?.dueAt || null,
				status: this.deriveSurveyStatus(a),
				raw: a
			});
		});

		this.vm.exerciseRows = exRows;
		this.vm.surveyRows = svRows;

		const all = [...exRows, ...svRows];
		this.vm.allRows = all;
		this.vm.hasRows = all.length > 0;

		const applyCommon = (arr: any[]) => {
			let out = arr;
			if (q) out = out.filter((r) => (r?.title || '').toString().toLowerCase().includes(q));
			return out.slice();
		};

		const filteredExercises = kind === 'SURVEY' ? [] : applyCommon(exRows);
		const filteredSurveys = kind === 'EXERCISE' ? [] : applyCommon(svRows);

		this.vm.pendingExercises = filteredExercises
			.filter((r) => (r?.status || '').toString().toLowerCase() !== 'completed')
			.sort((a, b) => this.sortKey(a.raw) - this.sortKey(b.raw));
		this.vm.pendingSurveys = filteredSurveys
			.filter((r) => (r?.status || '').toString().toLowerCase() !== 'completed')
			.sort((a, b) => this.sortKey(a.raw) - this.sortKey(b.raw));
		this.vm.historyExercises = filteredExercises
			.filter((r) => (r?.status || '').toString().toLowerCase() === 'completed')
			.sort((a, b) => this.historySortKey(b.raw) - this.historySortKey(a.raw));
		this.vm.historySurveys = filteredSurveys
			.filter((r) => (r?.status || '').toString().toLowerCase() === 'completed')
			.sort((a, b) => this.historySortKey(b.raw) - this.historySortKey(a.raw));

		if (this.vm.activeTab === 'history') {
			this.vm.filteredExercises = this.vm.historyExercises;
			this.vm.filteredSurveys = this.vm.historySurveys;
		} else {
			this.vm.filteredExercises = this.vm.pendingExercises;
			this.vm.filteredSurveys = this.vm.pendingSurveys;
		}
		this.vm.filtered = [...this.vm.filteredExercises, ...this.vm.filteredSurveys];
	}

	selectTab(tab: string) {
		this.vm.activeTab = tab === 'history' ? 'history' : 'pending';
		this.applyFilters();
	}

	openItem(row: any) {
		if (!row) return;
		const assignmentId = row.assignmentId;
		if (!assignmentId) return;

		// Include kind to avoid id collisions between exercise assignments and survey assignments.
		const kind = (row?.kind || '').toString().toLowerCase(); // 'exercise' | 'survey'
		if (kind === 'exercise' || kind === 'survey') {
			this.angularRoutingHelperService.setPath(['/my-exercises/resource', kind, assignmentId], false, false);
			return;
		}
		// Backwards fallback
		this.angularRoutingHelperService.setPath(['/my-exercises/resource', assignmentId], false, false);
	}
}
